import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { getCircleByInviteCode, joinByInviteCode } from '../services/circleService';
import { useCircle } from '../hooks/useCircle';

const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

export default function JoinByLinkPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, loading: authLoading, signInAnonymous } = useAuth();
  const { selectCircle } = useCircle();
  const [status, setStatus] = useState('loading');
  const [circle, setCircle] = useState(null);
  const [error, setError] = useState(null);
  const [guestName, setGuestName] = useState('');

  useEffect(() => {
    if (!code) return;
    const fetchCircle = async () => {
      try {
        const c = await getCircleByInviteCode(code);
        setCircle(c);
        setStatus('preview');
      } catch (err) {
        setError(err.message);
        setStatus('error');
      }
    };
    fetchCircle();
  }, [code]);

  const handleJoin = async () => {
    if (authLoading || !currentUser) return;
    setStatus('joining');
    try {
      const circleId = await joinByInviteCode(code, currentUser.uid, userProfile);
      selectCircle(circleId);
      setStatus('success');
      setTimeout(() => navigate('/art'), 1500);
    } catch (err) {
      if (err.message?.includes('already')) {
        setStatus('success');
        setTimeout(() => navigate('/art'), 1000);
      } else {
        setError(err.message);
        setStatus('error');
      }
    }
  };

  const handleJoinAsGuest = async () => {
    const displayName = guestName.trim() || 'Guest Artist';
    setStatus('joining');
    try {
      const user = await signInAnonymous();
      const profile = {
        displayName,
        isAnonymous: true,
      };
      const circleId = await joinByInviteCode(code, user.uid, profile);
      selectCircle(circleId);
      setStatus('success');
      setTimeout(() => navigate('/art'), 1500);
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  const openInApp = () => {
    window.location.href = `artpingpong://join/${code}`;
  };

  return (
    <Container>
      {status === 'loading' && (
        <>
          <Emoji>🔗</Emoji>
          <Title>Loading invite...</Title>
        </>
      )}

      {status === 'preview' && circle && (
        <>
          <LogoImg src="/AppLogoIcon.svg" alt="Art Ping Pong" />
          <Title>You're invited!</Title>
          <CircleCard>
            <CircleName>{circle.name}</CircleName>
            <CircleMeta>{circle.memberIds?.length || 1} member{(circle.memberIds?.length || 1) !== 1 ? 's' : ''}</CircleMeta>
          </CircleCard>

          {currentUser ? (
            <JoinButton onClick={handleJoin}>Join Circle</JoinButton>
          ) : (
            <>
              <NameInput
                type="text"
                placeholder="Your name (optional)"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                maxLength={30}
              />
              <JoinButton onClick={handleJoinAsGuest}>Join as Guest</JoinButton>
            </>
          )}

          {isIOS() && (
            <AppLink onClick={openInApp}>Open in Art Ping Pong app</AppLink>
          )}
        </>
      )}

      {status === 'joining' && (
        <>
          <Emoji>🔗</Emoji>
          <Title>Joining...</Title>
        </>
      )}

      {status === 'success' && (
        <>
          <Emoji>🎉</Emoji>
          <Title>Welcome to {circle?.name || 'the circle'}!</Title>
          <Text>Redirecting to your art chat...</Text>
        </>
      )}

      {status === 'error' && (
        <>
          <Emoji>😕</Emoji>
          <Title>Couldn't join</Title>
          <Text>{error}</Text>
          <BackButton onClick={() => navigate('/circles')}>Go to Circles</BackButton>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: calc(40px + env(safe-area-inset-top, 0px)) calc(24px + env(safe-area-inset-right, 0px)) 40px calc(24px + env(safe-area-inset-left, 0px));
  text-align: center;
`;

const LogoImg = styled.img`
  height: 80px;
  width: auto;
  margin-bottom: 16px;
`;

const Emoji = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const Text = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CircleCard = styled.div`
  margin: 16px 0 24px;
  padding: 20px 32px;
  border-radius: ${({ theme }) => theme.radius.large};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const CircleName = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const CircleMeta = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const NameInput = styled.input`
  width: 100%;
  max-width: 280px;
  padding: 14px 16px;
  margin-bottom: 12px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 16px;
  text-align: center;
  transition: all 0.2s;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }
`;

const JoinButton = styled.button`
  padding: 14px 40px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 17px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
  }
`;

const AppLink = styled.button`
  margin-top: 16px;
  padding: 10px 24px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const BackButton = styled.button`
  margin-top: 24px;
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 15px;
  font-weight: 600;
`;
